import {
  ApiError,
  assignEventStaff,
  listEventStaff,
  listUsers,
  unassignEventStaff,
} from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type {
  AssignEventStaffRequest,
  EventStaffAssignmentResponse,
  EventStatus,
  UserListItem,
} from '@jq4race/shared';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { isStaffingEditable, STAFF_ASSIGNABLE_ROLES } from '../constants';
import { AssignStaffForm } from './AssignStaffForm';
import { StaffTable } from './StaffTable';

const FEEDBACK_TIMEOUT_MS = 5000;
const USERS_PAGE_LIMIT = 100;

interface StaffingSectionProps {
  eventId: string;
  eventStatus: EventStatus;
}

export function StaffingSection({ eventId, eventStatus }: StaffingSectionProps) {
  const { t } = useTranslation('events');
  const [staff, setStaff] = useState<EventStaffAssignmentResponse[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [unassigningUserId, setUnassigningUserId] = useState<string | null>(null);

  const editable = isStaffingEditable(eventStatus);

  const reloadStaff = useCallback(async () => {
    setLoadingStaff(true);
    try {
      const res = await listEventStaff(eventId);
      setStaff(res.staff);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('staffingSection.loadStaffError') })
          : t('staffingSection.loadStaffError'),
      );
    } finally {
      setLoadingStaff(false);
    }
  }, [eventId, t]);

  const reloadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await listUsers({
        roles: STAFF_ASSIGNABLE_ROLES,
        page: 1,
        limit: USERS_PAGE_LIMIT,
      });
      setUsers(res.users);
    } catch (e) {
      setUsers([]);
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('staffingSection.loadUsersError') })
          : t('staffingSection.loadUsersError'),
      );
    } finally {
      setLoadingUsers(false);
    }
  }, [t]);

  useEffect(() => {
    void reloadStaff();
  }, [reloadStaff]);

  useEffect(() => {
    void reloadUsers();
  }, [reloadUsers]);

  useEffect(() => {
    if (!success && !error) return;
    const id = window.setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [success, error]);

  const usersById = new Map(users.map((u) => [u.id, u]));
  const staffedUserIds = new Set(staff.map((s) => s.userId));
  const eligibleUsers = users.filter((u) => u.active && !staffedUserIds.has(u.id));

  async function handleAssign(values: AssignEventStaffRequest) {
    try {
      const assigned = await assignEventStaff(eventId, values);
      const email = usersById.get(assigned.userId)?.email ?? assigned.userId;
      setSuccess(t('staffingSection.assignedSuccess', { email }));
      await reloadStaff();
    } catch (e) {
      throw e instanceof ApiError ? new LocalizedError(translateApiError(e, t)) : e;
    }
  }

  async function handleUnassign(assignment: EventStaffAssignmentResponse) {
    setUnassigningUserId(assignment.userId);
    try {
      await unassignEventStaff(eventId, assignment.userId);
      const email = usersById.get(assignment.userId)?.email ?? assignment.userId;
      setSuccess(t('staffingSection.unassignedSuccess', { email }));
      await reloadStaff();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('staffingSection.unassignError') })
          : t('staffingSection.unassignError'),
      );
    } finally {
      setUnassigningUserId(null);
    }
  }

  return (
    <div className="space-y-6">
      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      {editable ? (
        <div className="space-y-4 rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 sm:p-6">
          <AssignStaffForm
            eligibleUsers={eligibleUsers}
            loadingUsers={loadingUsers}
            onSubmit={handleAssign}
          />
        </div>
      ) : (
        <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3 text-[13px] text-jq-graphite">
          {t('staffingSection.readonlyNote')}
        </div>
      )}

      <StaffTable
        staff={staff}
        users={usersById}
        loading={loadingStaff}
        onUnassign={handleUnassign}
        unassigningUserId={unassigningUserId}
        editable={editable}
      />
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────
// Mirrors GuestListSection's dismissible auto-expiring banner exactly.

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
